import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@medflex/components/stories/Button';
import { useTranslation } from 'react-i18next';
import TextSnippetsPopup from '@medflex/components/stories/TextSnippetsPopup';
import Textarea from 'react-textarea-autosize';
import { showSuccessToast, showAlertToast } from '@medflex/components/stories/Toast';
import { TRACKING_ACTIONS, trackEvent } from '@medflex/utils/Tracking';
import useIsWideScreen from '@medflex/hooks/useIsWideScreen';
import Logger from '@medflex/utils/Logger';
import useSnippets from '../../hooks/useSnippets';
import useMessages from '../../hooks/useMessages';
import useModal from '../../hooks/useModal';
import useSessionChats from '../../hooks/useSessionChats';

const MessageComposer = ({ selectedContacts, message, setMessage, onBack }) => {
  const { t } = useTranslation('physician');
  const textRef = useRef();
  const isWideScreen = useIsWideScreen();
  const { close } = useModal();
  const { sendMulticast: sendContactMulticast } = useMessages();
  const { sendMulticast: sendSessionMulticast } = useSessionChats();
  const { snippets } = useSnippets();
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [isComposing, setIsComposing] = useState(false);

  useEffect(() => {
    if (isWideScreen && textRef.current) {
      setTimeout(() => {
        textRef.current.focus();
        textRef.current.setSelectionRange(message.length, message.length);
      });
    }
  }, [textRef, isWideScreen]);

  const sendMessages = async event => {
    try {
      const recipients = selectedContacts.reduce(
        ({ contacts, sessions }, { id, isTeam, user }) => ({
          ...(isTeam
            ? { sessions: [...sessions, id], contacts }
            : { sessions, contacts: [...contacts, user.id] }),
        }),
        { sessions: [], contacts: [] },
      );

      await Promise.all([
        recipients.contacts.length &&
          sendContactMulticast({
            text: message,
            recipients: recipients.contacts,
          }),

        recipients.sessions.length &&
          sendSessionMulticast({
            text: message,
            sessionChats: recipients.sessions,
          }),
      ]);

      close({ modal: 'multicast' });
      showSuccessToast(
        t('modals.search_contact.multicast.success_toast.title', {
          number: selectedContacts.length,
        }),
        null,
        {
          toastId: 'multicastSuccess',
        },
      );
      trackEvent(event, {
        action: TRACKING_ACTIONS.SUBMIT,
        actionText: `Multicast to ${selectedContacts.length} contacts succeeded`,
      });
    } catch (e) {
      Logger.error(e);
      showAlertToast(
        t('modals.search_contact.multicast.alert_toast.title'),
        t('modals.search_contact.multicast.alert_toast.text'),
        {
          toastId: 'multicastFailed',
        },
      );
      trackEvent(event, {
        action: TRACKING_ACTIONS.SUBMIT,
        actionText: `Multicast to ${selectedContacts.length} contacts failed`,
      });
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setIsComposing(true);
    await sendMessages(event);
    setIsComposing(false);
  };

  const handleInsertSnippet = ({ id }) => {
    const snippet = snippets.find(sn => sn.id === id);
    if (snippet.text) {
      setMessage(
        `${message.slice(0, selectionStart)}${snippet.text}${message.slice(
          selectionEnd,
          message.length,
        )}`,
      );
      if (textRef.current) {
        setTimeout(() => {
          textRef.current.setSelectionRange(
            selectionStart + snippet.text.length,
            selectionStart + snippet.text.length,
          );
          textRef.current.focus();
        });
      }
    }
  };

  const saveSelection = () => {
    if (textRef.current) {
      setSelectionStart(textRef.current.selectionStart);
      setSelectionEnd(textRef.current.selectionEnd);
    }
  };

  return (
    <>
      <div className="mb-4 bg-blue-100 p-2 text-center">
        {selectedContacts.length === 1
          ? t('modals.search_contact.contactsSelected_one', { count: selectedContacts.length })
          : t('modals.search_contact.contactsSelected_other', { count: selectedContacts.length })}
      </div>
      <div className="mb-4">{t('modals.search_contact.typePrompt')}</div>
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label className="w-full">
        <Textarea
          className="w-full resize-none rounded border border-slate-500 p-3 leading-tight placeholder-slate-500 outline-none focus:border-blue-500"
          value={message}
          onChange={e => setMessage(e.target.value)}
          onBlur={saveSelection}
          placeholder={t('modals.search_contact.multicast.placeholder')}
          minRows={6}
          maxLength="2000"
          ref={textRef}
        />
      </label>
      <div className="mb-8 text-slate-600">
        <TextSnippetsPopup snippets={snippets} onClick={handleInsertSnippet} />
      </div>
      <div className="flex justify-between">
        <Button kind="primary:outline" onClick={onBack}>
          {t('message_composer.back')}
        </Button>
        <Button
          className={[isComposing && 'pointer-events-none']}
          isDisabled={isComposing || message.trim() === ''}
          isLoading={isComposing}
          onClick={handleSubmit}
        >
          {t('message_composer.submit')}
        </Button>
      </div>
    </>
  );
};

MessageComposer.propTypes = {
  selectedContacts: PropTypes.array.isRequired,
  message: PropTypes.string.isRequired,
  setMessage: PropTypes.func.isRequired,
  onBack: PropTypes.func,
};

export default MessageComposer;
